import {
  deserializeMapDef,
  getMapById,
  hasMap,
  mapHash,
  type MapDef,
  type MapSource,
} from "@rietz/shared";

/**
 * Getting the raid's map. Two cases: STATIC (compiled into `@rietz/shared`,
 * instant) and DOWNLOAD (fetch the JSON, decode it, and REFUSE it unless its
 * hash matches the one the server named — a millimetre of difference is
 * prediction divergence forever, miserable to debug). Payloads are cached by
 * hash, so a reconnect costs nothing. The URL is resolved against the game
 * server's origin, derived from the WebSocket URL.
 */

export class MapLoadError extends Error {
  readonly detail: unknown;
  constructor(message: string, detail?: unknown) {
    super(message);
    this.name = "MapLoadError";
    this.detail = detail;
  }
}

/**
 * Download progress for the boot scrim. `loadedBytes` is DECOMPRESSED bytes so
 * far; `totalBytes` is `welcome`'s announced size, or `null` for an
 * indeterminate bar. Called once with `(0, total)` before the first chunk.
 */
export type MapProgressFn = (loadedBytes: number, totalBytes: number | null) => void;

export class MapLoader {
  private readonly httpOrigin: string;
  private readonly cache = new Map<string, MapDef>();

  constructor(serverUrl: string) {
    this.httpOrigin = httpOriginFor(serverUrl);
  }

  /** True when `load` for this source would resolve without touching the network. */
  isCached(source: MapSource): boolean {
    return source.kind === "static" || this.cache.has(source.hash);
  }

  async load(source: MapSource, mapId: string, onProgress?: MapProgressFn): Promise<MapDef> {
    if (source.kind === "static") {
      if (!hasMap(mapId)) {
        throw new MapLoadError(
          `server named the static map "${mapId}", which this client does not have`,
        );
      }
      return getMapById(mapId);
    }

    const cached = this.cache.get(source.hash);
    if (cached !== undefined) return cached;

    const url = new URL(source.url, this.httpOrigin).toString();
    const total = source.bytes ?? null;
    let text: string;
    try {
      const res = await fetch(url, { credentials: "omit" });
      if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
      text = await readBody(res, total, onProgress);
    } catch (err) {
      throw new MapLoadError(`could not download the map from ${url}: ${errText(err)}`, err);
    }

    // The bar is at 100 % but nothing has been PAINTED: without this yield the
    // decode (hundreds of ms on a town map) runs in the same task and the page
    // freezes.
    await yieldToDisplay();

    let map: MapDef;
    try {
      map = deserializeMapDef(text);
    } catch (err) {
      throw new MapLoadError(`the map at ${url} is not usable: ${errText(err)}`, err);
    }

    // Same again: hashing a few megabytes of map is the second long block, and
    // it must not be glued to the decode into one unpaintable task.
    await yieldToDisplay();

    const actual = mapHash(map);
    if (actual !== source.hash) {
      throw new MapLoadError(
        `map integrity check failed: server announced ${source.hash}, downloaded ${actual}. ` +
          `Refusing to play in a world the server does not share.`,
      );
    }

    this.cache.set(source.hash, map);
    return map;
  }
}

/**
 * Gives the browser a chance to paint before the next block of synchronous
 * work: a `requestAnimationFrame` gets INTO the frame, the `setTimeout` after
 * it lands after compositing. Degrades to the timeout under Node. The rAF wait
 * is bounded, since a HIDDEN tab gets no frames — alt-tabbing during the
 * download must not park the install.
 */
export async function yieldToDisplay(): Promise<void> {
  if (typeof requestAnimationFrame === "function") {
    await new Promise<void>((resolve) => {
      const fallback = setTimeout(resolve, 250);
      requestAnimationFrame(() => {
        clearTimeout(fallback);
        resolve();
      });
    });
  }
  await new Promise<void>((r) => setTimeout(r, 0));
}

/**
 * Reads the body, reporting progress chunk by chunk. Falls back to `res.text()`
 * when not streamable (old browser, mocked `fetch`).
 */
async function readBody(
  res: Response,
  total: number | null,
  onProgress?: MapProgressFn,
): Promise<string> {
  const body = res.body;
  if (onProgress === undefined || body === undefined || body === null) {
    return res.text();
  }

  onProgress(0, total);
  const reader = body.getReader();
  const decoder = new TextDecoder("utf-8");
  let text = "";
  let loaded = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    loaded += value.byteLength;
    // `stream: true` holds back a multi-byte sequence split across chunks; the
    // final flush below emits whatever it was still sitting on.
    text += decoder.decode(value, { stream: true });
    onProgress(loaded, total);
  }
  return text + decoder.decode();
}

function errText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** `ws://host:port/path` → `http://host:port`, `wss://…` → `https://…`. */
export function httpOriginFor(serverUrl: string): string {
  try {
    const u = new URL(serverUrl);
    u.protocol = u.protocol === "wss:" ? "https:" : u.protocol === "ws:" ? "http:" : u.protocol;
    return u.origin;
  } catch {
    // Not a URL we can parse (a bare host:port made it this far): fall back to
    // the page's own origin, which is the right guess for a same-host deploy.
    return typeof location === "undefined" ? "http://localhost" : location.origin;
  }
}
